import {
  type CreateQuerySubSourceRequest,
  type QuerySubSourceDetail,
  QuerySubSourceDetailSchema,
  type QuerySubSourceListQuery,
  type QuerySubSourceListResponse,
  QuerySubSourceListResponseSchema,
  type QuerySourceOption,
  QuerySourceOptionListSchema,
  type UpdateQuerySubSourceRequest,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Query Sub Source master API. Every response is validated with the shared
 * Zod schema so a server change can't sneak past the UI types.
 */

export const listQuerySubSources = async (
  query: QuerySubSourceListQuery,
): Promise<QuerySubSourceListResponse> => {
  const { data } = await apiClient.get<unknown>('/query-sub-sources', { params: query });
  return QuerySubSourceListResponseSchema.parse(data);
};

export const getQuerySubSource = async (id: string): Promise<QuerySubSourceDetail> => {
  const { data } = await apiClient.get<unknown>(`/query-sub-sources/${id}`);
  return QuerySubSourceDetailSchema.parse(data);
};

export const createQuerySubSource = async (
  payload: CreateQuerySubSourceRequest,
): Promise<QuerySubSourceDetail> => {
  const { data } = await apiClient.post<unknown>('/query-sub-sources', payload);
  return QuerySubSourceDetailSchema.parse(data);
};

export const updateQuerySubSource = async (
  id: string,
  payload: UpdateQuerySubSourceRequest,
): Promise<QuerySubSourceDetail> => {
  const { data } = await apiClient.patch<unknown>(`/query-sub-sources/${id}`, payload);
  return QuerySubSourceDetailSchema.parse(data);
};

export const deleteQuerySubSource = async (id: string): Promise<void> => {
  await apiClient.delete(`/query-sub-sources/${id}`);
};

export const bulkDeleteQuerySubSources = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/query-sub-sources/bulk-delete', { ids });
  return data;
};

export const bulkSetQuerySubSourcesStatus = async (
  ids: string[],
  isActive: boolean,
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/query-sub-sources/bulk-set-status', { ids, isActive });
  return data;
};

// ----- Parent source options -----

/** Slim { id, code, name } list from the query sources options endpoint. */
export const listQuerySourceOptions = async (): Promise<QuerySourceOption[]> => {
  const { data } = await apiClient.get<unknown>('/query-sources/options');
  return QuerySourceOptionListSchema.parse(data);
};
